import { faDatabase } from "@fortawesome/free-solid-svg-icons";
import useModalManager from "@hooks/useModalManager";
import { StatusBadge } from "components/MessageManagement/MessageList";
import moment from "moment";
import dynamic from "next/dynamic";
import { Blockquote, InlineTabBar } from "@ui";
import { useClubSlug } from "components/Clubs/hooks";
import { MemberInboxList } from "components/MessageManagement/Inbox/MemberInboxList";
import { useCheckPermissions } from "hooks/useCheckPermissions";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { Column, DataTable, DataTableContainer, TableActionButton } from "../../../DataTable";
import { UserInvoice } from "../../../UserInvoice/types";
import { ClubMember } from "../../types";

const MessageHistory = dynamic(() => import("../../../MessageManagement/MessageList/MessageHistory"));

interface Props {
    member: ClubMember;
}

interface MailBounce {
    email: string;
    bounce_sub_type: string | null;
    diagnostic: string | null;
    bounced_at: string;
    mail_message_id: number | null;
}

const ClubMemberMessagesList = ({ member }: Props) => {
    const { t } = useTranslation("message-management");
    const { addModal } = useModalManager();
    const clubSlug = useClubSlug();
    const { hasAccess: canSeeInbox } = useCheckPermissions({ clubSlug, permission: "message.inbox.view" });
    const [tab, setTab] = useState<"sent" | "received">("sent");
    const { data: bounceData } = useSWR<{ data: MailBounce | null }>(`club-member/${member.id}/mail-bounce`);
    const bounce = bounceData?.data;
    const columns: Column<UserInvoice>[] = useMemo(() => [
        {
            name: t("list.columns.to"),
            uid: "to",
            allowsSorting: true,
            renderer: (value) => {
                if (Array.isArray(value) && value.length > 0) {
                    const address = value?.[0];
                    let addressString = `${address?.address || ""}`;
                    if (address?.name) addressString += ` - ${address?.name}`;
                    if (value.length > 1) addressString += ` | + ${value.length - 1}`;

                    return addressString;
                }

                return Object.entries(value)
                    .map(([addr, name]) => addr + (name ? ` - ${name}` : ""))
                    .join(" | ");
            },
        },
        { name: t("list.columns.subject"), uid: "subject", allowsSorting: true },
        {
            name: t("list.columns.lastStatus"),
            uid: "status",
            renderer(value, item?) {
                const last = value?.[0];
                return <StatusBadge status={last} messageId={item.id} />;
            },
        },
        {
            name: t("list.columns.sentAt"),
            uid: "created_at",
            allowsSorting: true,
            renderer(value, item?) {
                return moment(value).format("lll");
            },
        },
        { name: t("list.columns.actions"), uid: "actions", align: "end" },
    ], [t]);

    const tabs = useMemo(
        () => [
            { key: "sent", label: t("nav.sent") },
            { key: "received", label: t("memberInbox.received") },
        ],
        [t]
    );
    const tabBar = canSeeInbox && (
        <InlineTabBar className="tw-mb-4" activeTab={tab} onChange={(key) => setTab(key as "sent" | "received")} tabs={tabs} />
    );

    const openMessageStatusHistory = useCallback(
        (messageId: number) =>
            addModal({
                title: t("list.statusHistoryTitle"),
                content: <MessageHistory messageId={messageId} />,
                size: 50,
                show: true,
            }),
        [t]
    );

    if (canSeeInbox && tab === "received") {
        return (
            <>
                {tabBar}
                <MemberInboxList clubSlug={clubSlug} clubMemberId={member.id} />
            </>
        );
    }

    return (
        <>
            {tabBar}
            {bounce && (
                <Blockquote color="warning" className="tw-mb-4">
                    <h3>{t("memberBounce.title")}</h3>
                    <p className="tw-mb-0">
                        {t("memberBounce.text", { email: bounce.email, date: moment(bounce.bounced_at).format("LL") })}
                    </p>
                    {bounce.diagnostic && (
                        <p className="tw-mb-0 tw-mt-2 tw-text-xs tw-text-accents-6">
                            {t("memberBounce.diagnostic", { diagnostic: bounce.diagnostic })}
                        </p>
                    )}
                </Blockquote>
            )}
            <DataTableContainer>
            <DataTable
                hideSearchBar
                className="tw-h-full"
                fetchUrl={`club-member/${member.id}/messages`}
                columns={columns}
                tableProps={{
                    showSelectionCheckboxes: false,
                    sticked: true,
                    compact: false,
                }}
                rowActions={(item) => (
                    <TableActionButton.Container justify="flex-end">
                        <TableActionButton icon={faDatabase} onPress={() => openMessageStatusHistory(item.id)}>
                            {t("list.statusHistoryButton")}
                        </TableActionButton>
                    </TableActionButton.Container>
                )}
            />
            </DataTableContainer>
        </>
    );
};

export default ClubMemberMessagesList;
